import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { WireApiService } from '@frontend/shared';
import { CreateUserRequest, ErrorResponse, User, UserListResponse, UserRole } from '@clientwire/api';
import { TranslateService } from '@ngx-translate/core';
import { DialogRef } from '@angular/cdk/dialog';
import { UserDialogComponent } from '@clientwire/settings';
import { ToastrService } from 'ngx-toastr';

@Injectable({ providedIn: 'root' })
export class UsersService {
  readonly #wireApiService = inject(WireApiService);
  readonly #translateService = inject(TranslateService)
  readonly #toastr = inject(ToastrService)

  readonly usersLoading = signal(false);
  readonly users: WritableSignal<User[] | []> = signal([]);
  readonly error: WritableSignal<string | null> = signal(null);


  loadUsers(): void {
    this.usersLoading.set(true);

    this.#wireApiService.usersApi.listUsers()
      .then((response: UserListResponse) => {
        this.users.set(response.users);
      })
      .catch(async (error) => {
        console.error('Error loading users:', error);

        // Extract error message from backend response
        let errorMessage = this.#translateService.instant('SETTINGS.USERS.ERROR_LOADING');

        try {
          if (error.response) {
            const errorData: ErrorResponse = await error.response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
          this.error.set(errorMessage)
        } catch (e) {
          console.error('Error parsing error response:', e);
        }
      });

    this.usersLoading.set(false)
  }

  createUser(userData: any, dialogRef: DialogRef<UserDialogComponent>): void {
    const createUserRequest: CreateUserRequest = {
      email: userData.email,
      password: userData.password,
      firstname: userData.firstname || null,
      lastname: userData.lastname || null,
      role: userData.role as UserRole || null
    };

    // Get dialog component instance
    const dialogComponent = dialogRef.componentInstance as UserDialogComponent;
    dialogComponent.setLoading(true);

    this.#wireApiService.usersApi.createUser({ createUserRequest })
      .then(() => {
        this.loadUsers();
        dialogRef.close();
        this.#toastr.success(
          this.#translateService.instant('SETTINGS.USERS.USER_ADDED_MESSAGE'),
          this.#translateService.instant('SETTINGS.USERS.USER_ADDED_TITLE')
        );
      })
      .catch(async (error: any) => {
        console.error('Error creating user:', error);

        let errorMessage = this.#translateService.instant('SETTINGS.USERS.ERROR_ADDING_MESSAGE');

        try {
          if (error.response) {
            const errorData: ErrorResponse = await error.response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
        } catch (e) {
          console.error('Error parsing error response:', e);
        }

        // Set the error message in the dialog
        dialogComponent.setError(errorMessage);
      });
  }

  updateUser(userId: string, userData: any, dialogRef: DialogRef<UserDialogComponent>): void {
    // Create a patch map with only the fields we want to update
    const patchMap: { [key: string]: any } = {};

    // Add fields to the patch map only if they exist in userData
    if (userData.email) {
      patchMap['email'] = userData.email;
    }

    if (userData.firstname !== undefined) {
      patchMap['firstname'] = userData.firstname;
    }

    if (userData.lastname !== undefined) {
      patchMap['lastname'] = userData.lastname;
    }

    if (userData.role) {
      patchMap['role'] = userData.role === UserRole.Owner ? 'OWNER' : 'USER';
    }

    // Get dialog component instance
    const dialogComponent = dialogRef.componentInstance as UserDialogComponent;
    dialogComponent.setLoading(true);

    this.#wireApiService.usersApi.updateUser({
      userId,
      requestBody: patchMap
    })
      .then(() => {
        this.loadUsers();
        dialogRef.close();
        this.#toastr.success(
          this.#translateService.instant('SETTINGS.USERS.USER_UPDATED_MESSAGE'),
          this.#translateService.instant('SETTINGS.USERS.USER_UPDATED_TITLE')
        );
      })
      .catch(async (error) => {
        console.error('Error updating user:', error);

        // Extract error message from backend response
        let errorMessage = this.#translateService.instant('SETTINGS.USERS.ERROR_UPDATING_MESSAGE');

        try {
          if (error.response) {
            const errorData: ErrorResponse = await error.response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
        } catch (e) {
          console.error('Error parsing error response:', e);
        }

        dialogComponent.setError(errorMessage);
      });
  }

  deleteUser(id: string) {
    this.#wireApiService.usersApi.deleteUser({ userId: id })
      .then(() => {
        this.loadUsers();
        this.#toastr.success(
          this.#translateService.instant('SETTINGS.USERS.USER_DELETED_MESSAGE'),
          this.#translateService.instant('SETTINGS.USERS.USER_DELETED_TITLE')
        );
      })
      .catch(async (error: any) => {
        console.error('Error deleting user:', error);

        // Extract error message from backend response
        let errorMessage = this.#translateService.instant('SETTINGS.USERS.ERROR_DELETING_MESSAGE');
        const errorTitle = this.#translateService.instant('SETTINGS.USERS.ERROR_DELETING_TITLE');

        try {
          if (error.response) {
            const errorData: ErrorResponse = await error.response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
        } catch (e) {
          console.error('Error parsing error response:', e);
        }

        this.#toastr.error(errorMessage, errorTitle);
      });

  }

  unlockConversations(id: string) {
    this.#wireApiService.conversationsApi
      .unlockConversationsFromUsers({ userId: id })
      .then(() => {
        this.#toastr.success(
          this.#translateService.instant(
            'SETTINGS.USERS.UNLOCK_ALL_CONVERSATIONS.UNLOCK_SUCCESSFUL_MESSAGE'
          )
        );
      })
      .catch(() => {
        this.#toastr.error(
          this.#translateService.instant(
            'SETTINGS.USERS.UNLOCK_ALL_CONVERSATIONS.UNLOCK_FAILED_MESSAGE'
          )
        );
      });
  }

}
